// In PushNotification.jsx
import React from "react";
import { CheckCircle, AlertTriangle, Info, XCircle, X, ExternalLink } from "lucide-react";

const PushNotification = ({ alert, onRemove }) => {
  // Function to render the appropriate icon based on notification type
  const renderIcon = (type) => {
    switch (type) {
      case "success":
        return <CheckCircle size={20} color="#2E7D32" />;
      case "warn":
        return <AlertTriangle size={20} color="#ED6C02" />;
      case "error":
        return <XCircle size={20} color="#D32F2F" />;
      case "info":
      default:
        return <Info size={20} color="#0288D1" />;
    }
  };

  // Parse message to extract links and text content
  const parseMessageContent = (message) => {
    if (!message) return { text: "", links: [] };
    
    // Extract links from HTML content
    const linkRegex = /<a\s+(?:[^>]*?\s+)?href="([^"]*)"(?:[^>]*?)>(.*?)<\/a>/g;
    const links = [];
    let match;
    
    while ((match = linkRegex.exec(message)) !== null) {
      links.push({
        url: match[1],
        text: match[2]
      });
    }
    
    // Remove HTML tags to get plain text
    const text = message.replace(/<[^>]*>?/gm, '');
    
    return { text, links };
  };

  const { text, links } = parseMessageContent(alert.message);

  // Handle click on the notification container
  const handleNotificationClick = (e) => {
    // Don't trigger if the close button was clicked
    if (e.target.closest('button[aria-label="Remove notification"]')) {
      return;
    }
    
    // Don't trigger if clicking a link button
    if (e.target.closest('.notification-link-button')) {
      return;
    }

    // If there's only one link, open it on notification click
    if (links.length === 1) {
      window.open(links[0].url, '_blank', 'noopener,noreferrer');
    }
  };

  // Open a specific link
  const openLink = (url, e) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`mb-3 p-3 rounded relative border-l-4 cursor-pointer hover:opacity-90 transition-opacity ${
        alert.type === "success" ? "bg-green-50 border-green-700" : 
        alert.type === "warn" ? "bg-orange-50 border-orange-600" : 
        alert.type === "error" ? "bg-red-50 border-red-700" : 
        "bg-blue-50 border-blue-600"
      }`}
      onClick={handleNotificationClick}
    >
      <button
        className="absolute top-0 right-0 p-1 m-1 bg-transparent border-none"
        onClick={(e) => {
          e.stopPropagation(); // Prevent notification click event
          onRemove();
        }}
        aria-label="Remove notification"
      >
        <X size={16} />
      </button>
      
      <div className="flex items-start">
        <div className="mr-3 mt-1">
          {renderIcon(alert.type)}
        </div>
        <div className="flex-grow-1">
          {alert.title && (
            <div className="font-bold mb-1">{alert.title}</div>
          )}
          
          {/* Display the message text */}
          <div className="whitespace-pre-line">
            {text}
          </div>
          
          {/* If there are links, show them as buttons */}
          {links.length > 0 && (
            <div className="mt-2">
              {links.map((link, index) => (
                <button
                  key={index}
                  onClick={(e) => openLink(link.url, e)}
                  className="notification-link-button inline-flex items-center px-3 py-1 mt-1 mr-2 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                >
                  <ExternalLink size={14} className="mr-1" />
                  {link.text || "View Attachment"}
                </button>
              ))}
            </div>
          )}
          
          {alert.timestamp && (
            <div className="text-gray-500 mt-1 text-xs">
              {alert.timestamp}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PushNotification;